// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// devextreme imports
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// 3rd party
import { cloneDeep } from 'lodash';
import { plainToClass } from 'class-transformer';

// cc-common imports
import { DxWidgetUtils, IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// service imports
import { UserService } from '../../../../_services/data-services/user.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { APAdjustmentService } from '../../_services/accounts-payable/ap-adjustment.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { CirabooksHelperService } from '../../../_services/cirabooks-helper.service';

// model imports
import { APAdjustmentEditorResultDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-editor-result-dto';
import { APAdjustmentParameterDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-parameter-dto';
import { APAdjustmentRawDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-raw-dto';
import { DimensionOptionCodeRawDto } from '../../_models/common/dimensions/dimension-option-code-raw-dto';
import { SubCategoryByGlAccountParametersDto } from '../../_models/common/subcategory-by-gl-account-parametere-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { APAdjustmentItemRawDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-item-raw-dto';
import { DimensionSelectorResultDto } from '../../_models/common/dimensions/dimension-selector-result-dto';
import { GLAccountSelectorResultDto } from '../../_models/common/gl-account/glaccount-selector-result-dto';
import { GLAccountLabelSelectorResultDto } from '../../_models/common/gl-account/gl-account-label-selector-result-dto';
import { UserInfoResultDto } from '../../../../_models/user-info-result-dto';
import { APAdjustmentAttachmentRawDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-attachement-raw-dto';
import { UnappliedPaymentResultDto } from '../../../community-cirabooks-module/accounts-payable/_models/unapplied-payments/unapplied-payment-result-dto';
import { APAdjustmentEditorForApplyPaymentWithAPCMParameterDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-editor-for-apply-payment-with-apcm-parameter-dto';
import { ApplyPaymentWithAPCMParameterDto } from '../../_models/accounts-payable/ap-adjustment/apply-payment-with-apcm-parameter-dto';
import { APAdjustmentEditorForApplyPaymentWithAPCMResultDto } from '../../_models/accounts-payable/ap-adjustment/ap-adjustment-editor-for-apply-payment-with-apcm-result-dto';
import { DimensionOptionResultDto } from '../../_models/common/dimensions/dimension-option-result-dto';
import { AttachmentRawDto } from '../../../../management-common/_models/attachment-raw-dto';
import { VendorLineItemTemplateRawDto } from '../../../../shared-workflow-module/_models/vendor-lineItem-template-raw-dto';

// constants imports
import { ActionConstants } from '../../../../_constants/action-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { URLConstants } from '../../../../_constants/url-constants';

@Component({
    selector: 'app-ap-adjustment-editor',
    templateUrl: './ap-adjustment-editor.component.html',
    styleUrls: ['./ap-adjustment-editor.component.scss']
})
export class APAdjustmentEditorComponent implements OnInit {
    @ViewChild('addAPAdjustmentPopup', { static: false }) addAPAdjustmentPopup: DxPopupComponent;
    @ViewChild('apAdjustmentEditorGrid', { static: false }) apAdjustmentEditorGrid: DxDataGridComponent;
    @ViewChild('apAdjustmentMessageBox', { static: false }) apAdjustmentMessageBox: MessageBoxComponent;

    @Input() managementCompanyId: number;
    @Input() communityId: number;
    @Input() apAdjustmentId: number = null;
    @Input() applyPaymentWithAPCM = false;
    @Input() visible = false;

    @Output() onClose = new EventEmitter<boolean>();

    forceRefresh = false;
    loadingVisible = false;

    adjustmentTitle: string;
    isEditMode = false;
    isLimitedEdit: boolean;

    selectedAccountType: string;
    apAdjustmentEditor: APAdjustmentEditorResultDto;
    apAdjustment: APAdjustmentRawDto;
    originalAdjusment: APAdjustmentRawDto;
    modifiedAPAdjustment: APAdjustmentRawDto;
    dimensionSource: Array<DimensionSelectorResultDto>;
    glAccountNumberSource: Array<GLAccountSelectorResultDto>;
    glAccountLabelSource: Array<GLAccountLabelSelectorResultDto>;
    defaultOffsetGLAccountNumber: string;

    accountTypeSource: Array<any> = [];

    vendorGridBoxValue: Array<number> = [];
    vendorColumns: Array<any> = [];

    isInternalUser = false;
    userInfo: UserInfoResultDto;

    addLineItemButtonOptions = '';

    iconCancel = IconConstants.CANCEL;
    iconSave = IconConstants.SAVE;
    iconDelete = IconConstants.DELETE;

    currencyFormat: string;
    totalAmount: number;
    unappliedPayment: UnappliedPaymentResultDto;
    requireDimensionMatch = false;
    paymentRequiredDimensions: DimensionOptionResultDto[];

    apAdjustmentAttachments: Array<AttachmentRawDto> = [];
    disabledAdjustmentType = false;

    constructor(
        private readonly apAdjustmentService: APAdjustmentService,
        private readonly errorService: ServerErrorService,
        private readonly userService: UserService,
        private readonly helperService: HelperService,
        private readonly precisionService: PrecisionService,
        private readonly cirabooksHelperService: CirabooksHelperService
    ) {
        this.onNewLineItemClick = this.onNewLineItemClick.bind(this);
    }

    ngOnInit() {
        this.isInternalUser = this.userService.isInternal();

        this.userInfo = this.userService.getCurrentUserInfo();
        this.currencyFormat = this.precisionService.getCurrencyFormat();

        this.configureEditor();
        this.configureAccountTypeSource();

        if (Utils.notNullAndDefined(this.managementCompanyId)) {
            this.show();
            this.getAPAdjustmentEditor();
        }
    }

    show(): void {
        this.visible = true;
    }

    onShown(): void {
        DxWidgetUtils.instantRepaint(this.addAPAdjustmentPopup);
        DxWidgetUtils.instantRepaint(this.apAdjustmentEditorGrid);
    }

    onHidden(): void {
        this.closeAPAdjustmentEditor();
    }

    onCancelClick(): void {
        this.closeAPAdjustmentEditor();
    }

    onSaveClick(): void {
        this.apAdjustmentEditorGrid.instance.saveEditData().then(() => {
            this.prepareLineItems();
        });
    }

    saveAdjustment(): void {
        // In case of DELETE crud Action following code is required to remove null dimensionOptionCode
        this.modifiedAPAdjustment.items.forEach((item) => {
            item.dimensions = item.dimensions.filter((d) => Utils.notNullOrEmpty(d.dimensionOptionCode));
        });
        if (Utils.isFalse(this.isEditMode)) {
            if (Utils.isTrue(this.isLimitedEdit) && Utils.notNullOrEmpty(this.apAdjustmentId)) {
                this.editAPAdjustment();
            } else if (Utils.isFalse(this.applyPaymentWithAPCM)) {
                this.addAPAdjustment();
            } else {
                this.createAPAdjustmentAndApplyPayment();
            }
        } else {
            if (Utils.notNullOrEmpty(this.apAdjustmentId)) {
                this.editAPAdjustment();
            } else {
                this.addAPAdjustment();
            }
        }
    }

    onVendorPayeeSelection(selectedObject: any) {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const vendorRow = selectedObject.selectedRow[0];
            this.apAdjustment.vendorId = vendorRow.id;
        } else {
            this.apAdjustment.vendorId = null;
        }
    }

    onNewLineItemClick(): void {
        this.addRawLineItem();
    }

    onAccountSelection(event: any, cellInfo: any) {
        this.selectedAccountType = event.accountType;
        cellInfo.setValue(event.title);
        cellInfo.data.accountLabelCode = event.accountLabelCode;
        cellInfo.data.glAccountNumber = event.glAccountNumber;
        cellInfo.data.offsetGLAccountNumber = event.offsetGLAccountNumber;
        cellInfo.data.disableSubCategory = event.disableSubCategory;
        cellInfo.data.subCategoryCode = event.subCategoryCode;
        cellInfo.data.tab = event.accountType;

        if (
            Utils.isTrue(this.apAdjustmentEditor.entityUsesSubCategory) &&
            Utils.notNullAndDefined(event.glAccountNumber)
        ) {
            this.populateSubcategories(event.glAccountNumber, cellInfo.data, null, event.accountType, false, true);
        }
    }

    onEditorContentReady(event: any): void {
        event.component.filter(['crudAction', '<>', ActionConstants.DELETE]);
    }

    onRowUpdated(event: any): void {
        const rowData = event.data;
        rowData.crudAction = Utils.notNullOrEmpty(rowData.crudAction) ? rowData.crudAction : ActionConstants.EDIT;

        let totalChargeAmount = 0;
        let totalChargeDueAmount = 0;

        this.apAdjustment.items.forEach((item) => {
            const dimensions: DimensionOptionCodeRawDto[] = [];

            if (
                (Utils.notNullAndDefined(rowData.apAdjustmentItemId) && // if we add a new item automatically such as from vendor template, we don't want to go into this if logic block
                    Utils.equals(item.apAdjustmentItemId, rowData.apAdjustmentItemId)) ||
                (Utils.notNullAndDefined(rowData.itemId) && // if we have more than 1 line item in add mode, we do not want to copy data from the current editing row to the other rows of the adjustment
                    Utils.notNullAndDefined(item['itemId']) &&
                    Utils.equals(item['itemId'], rowData.itemId))
            ) {
                this.dimensionSource.forEach((row) => {
                    const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                    obj.dimensionCode = row.code;
                    obj.dimensionOptionCode = rowData[row.code];
                    dimensions.push(obj);
                });
                item.dimensions = dimensions;

                // update open balance for add or full edit
                if (
                    (Utils.isTrue(this.isEditMode) && Utils.isTrue(this.apAdjustmentEditor.allowEdit)) ||
                    Utils.equalsIgnoreCase(item.crudAction, ActionConstants.ADD)
                ) {
                    item.openBalance = item.amount;
                }
            }

            totalChargeAmount += Utils.equalsIgnoreCase(item.crudAction, ActionConstants.DELETE) ? 0 : item.amount;
            totalChargeDueAmount += Utils.equalsIgnoreCase(item.crudAction, ActionConstants.DELETE)
                ? 0
                : item.openBalance;
        });

        if (Utils.isTrue(this.isEditMode)) {
            this.apAdjustment.amount = this.precisionService.getRoundedNumber(totalChargeAmount);
            this.apAdjustment.openBalance = this.precisionService.getRoundedNumber(totalChargeDueAmount);
        }
    }

    onDeleteFromGrid(item: APAdjustmentItemRawDto): void {
        if (Utils.isNullOrUndefined(item.apAdjustmentItemId)) {
            this.apAdjustment.items = this.apAdjustment.items.filter((d) =>
                Utils.notEquals(d['itemId'], item['itemId'])
            );
        } else {
            this.apAdjustment.items.forEach((ele) => {
                if (Utils.equals(ele.apAdjustmentItemId, item.apAdjustmentItemId)) {
                    ele.crudAction = ActionConstants.DELETE;
                }
            });
        }

        this.apAdjustmentEditorGrid.instance.filter(['crudAction', '<>', ActionConstants.DELETE]);
        this.apAdjustmentEditorGrid.instance.refresh();
    }

    onAttachmentUpdate(attachments: any[]): void {
        const apAdjustmentAttachments: Array<APAdjustmentAttachmentRawDto> = [];
        attachments.forEach((attachment) => {
            let obj: APAdjustmentAttachmentRawDto = new APAdjustmentAttachmentRawDto();
            obj = Object.assign(obj, attachment);
            obj.crudAction = attachment.action;
            obj.fileDataBase64 = attachment.fileBase64String;
            delete obj['action'];
            delete obj['fileBase64String'];
            delete obj['customerId'];
            if (Utils.notNullAndDefined(obj.crudAction)) {
                apAdjustmentAttachments.push(obj);
            }
        });

        this.apAdjustment.attachments = apAdjustmentAttachments;
    }

    private prepareAttachments(): void {
        this.apAdjustmentAttachments = [];
        if (Utils.notNullOrEmpty(this.apAdjustment.attachments)) {
            this.apAdjustment.attachments.forEach((attachment) => {
                const downloadUrl =
                    URLConstants.ccCBAPAdjustmentApiUrl +
                    `/attachment?managementCompanyId=${this.managementCompanyId}&communityId=${this.communityId}&attachmentId=${attachment.fileId}&apAdjustmentId=${this.apAdjustmentId}`;
                const obj: AttachmentRawDto = new AttachmentRawDto();
                obj.fileId = attachment.fileId;
                obj.fileBase64String = attachment.fileDataBase64;
                obj.fileName = attachment.fileName;
                obj.action = attachment.crudAction;
                obj.fileUrl = downloadUrl;
                this.apAdjustmentAttachments.push(obj);
            });
        }
    }

    private configureEditor(): void {
        this.vendorColumns = [
            {
                dataField: 'name',
                dataType: 'string',
                caption: 'Name'
            },
            {
                dataField: 'isActive',
                dataType: 'boolean',
                caption: 'Active',
                cellTemplate: 'trueValueCellTemplate'
            }
        ];

        this.addLineItemButtonOptions = JSON.stringify({
            beforeIcons: [IconConstants.ADD],
            label: 'New Line Item'
        });
    }

    private configureTitle(): void {
        if (Utils.isTrue(this.isEditMode) && Utils.notNullOrEmpty(this.apAdjustmentId)) {
            this.adjustmentTitle = `Edit AP Adjustment #${this.apAdjustmentId}`;
        } else if (Utils.isTrue(this.isLimitedEdit) && Utils.isFalse(this.isEditMode)) {
            this.adjustmentTitle = `Edit AP Adjustment #${this.apAdjustmentId} (Limited Edit)`;
        } else {
            this.adjustmentTitle = 'Add AP Adjustment';
        }
    }

    private prepareLineItems(): void {
        let isLineItemPrepared = true;

        const modifiedLineItems: Array<APAdjustmentItemRawDto> = [];
        this.modifiedAPAdjustment = new APAdjustmentRawDto();
        this.totalAmount = 0;
        if (Utils.notNullOrEmpty(this.apAdjustment.items)) {
            for (const apAdjustmentLineItem of this.apAdjustment.items) {
                const dimensionKeysToDelete: string[] = [];
                const dimensionToDelete: any[] = [];

                // const rawItem = Object.assign(new APAdjustmentItemRawDto(), apAdjustmentLineItem);
                // deep copy without affecting the original data from apAdjustmentLineItem
                let rawItem = plainToClass(
                    APAdjustmentItemRawDto,
                    JSON.parse(JSON.stringify(apAdjustmentLineItem)) as APAdjustmentItemRawDto
                );

                if (Utils.equalsIgnoreCase(apAdjustmentLineItem.crudAction, ActionConstants.DELETE)) {
                    rawItem = this.originalAdjusment.items.find((i) =>
                        Utils.equals(i.apAdjustmentItemId, apAdjustmentLineItem.apAdjustmentItemId)
                    );
                    rawItem.crudAction = ActionConstants.DELETE;
                } else {
                    if (rawItem.amount > 0) {
                        rawItem.crudAction = Utils.notNullAndDefined(rawItem.crudAction) ? rawItem.crudAction : '';
                        this.totalAmount += Utils.equalsIgnoreCase(rawItem.crudAction, ActionConstants.DELETE)
                            ? 0
                            : rawItem.amount;
                        if (Utils.notNullOrEmpty(rawItem.dimensions)) {
                            rawItem.dimensions.forEach((dim) => {
                                const isDimRequired = this.isDimensionMandatory(dim);
                                dim.dimensionOptionCode = rawItem[dim.dimensionCode];
                                dimensionKeysToDelete.push(dim.dimensionCode);

                                if (Utils.isFalse(isDimRequired) && Utils.isNullOrEmpty(dim.dimensionOptionCode)) {
                                    dimensionToDelete.push(dim.dimensionCode);
                                }
                            });

                            dimensionToDelete.forEach((dim) => {
                                const rawIndex = rawItem.dimensions.findIndex((d) =>
                                    Utils.equalsIgnoreCase(d.dimensionCode, dim)
                                );
                                if (Utils.notEquals(rawIndex, -1)) {
                                    rawItem.dimensions.splice(rawIndex);
                                }
                            });
                        }
                        dimensionKeysToDelete.forEach((key) => {
                            delete rawItem[key];
                        });
                        delete rawItem['subCategorySource'];
                        delete rawItem['selectedAccountName'];
                        delete rawItem['disableSubCategory'];
                        delete rawItem['accountType'];
                        delete rawItem['itemId'];
                    } else {
                        this.apAdjustmentMessageBox.showErrorMessage(
                            'Error',
                            `Line Item Amount must be different from zero for ItemId #${rawItem['itemId']}`
                        );
                        isLineItemPrepared = false;
                        break;
                    }
                }

                modifiedLineItems.push(rawItem);
            }
        }
        if (Utils.isTrue(isLineItemPrepared)) {
            this.modifiedAPAdjustment = Object.assign(this.modifiedAPAdjustment, this.apAdjustment);
            this.modifiedAPAdjustment.items = modifiedLineItems;
            this.modifiedAPAdjustment.amount = this.precisionService.getRoundedNumber(this.totalAmount);
            this.modifiedAPAdjustment.openBalance = this.precisionService.getRoundedNumber(this.totalAmount);
            const formStatus = this.checkValidity();
            const resultStatus = this.checkLineItemValidity();
            if (Utils.isFalse(formStatus.isValid)) {
                isLineItemPrepared = false;
                this.apAdjustmentMessageBox.showErrorMessage('Error', formStatus.message);
            } else if (Utils.isFalse(resultStatus.isValid)) {
                isLineItemPrepared = false;
                this.apAdjustmentMessageBox.showErrorMessage('Error', resultStatus.message);
            } else {
                this.saveAdjustment();
            }
        } else {
            this.resetAPAdjustmentRawItem();
        }
    }

    private isDimensionMandatory(dimensionToCheck: DimensionOptionCodeRawDto): boolean {
        const dimension = this.dimensionSource.find((d) =>
            Utils.equalsIgnoreCase(d.code, dimensionToCheck.dimensionCode)
        );
        return dimension.required;
    }

    private resetAPAdjustmentRawItem(): void {
        this.prepareDimensions();
        if (Utils.isTrue(this.apAdjustmentEditor.entityUsesSubCategory)) {
            this.getSubCategories();
        }
    }

    private editAPAdjustment(): void {
        this.loadingVisible = true;
        // Note: As per discussion with Vinay on 28/06/2024, pass attachment with crudAction available. We should avoid sending those which crudAction is empty/null.
        this.modifiedAPAdjustment.attachments = this.modifiedAPAdjustment.attachments.filter((d) =>
            Utils.notNullOrEmpty(d.crudAction)
        );

        this.apAdjustmentService.editAPAdjustment(this.modifiedAPAdjustment).subscribe(
            () => {
                this.showSuccessMessage('AP Adjustment updated successfully.');
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private addAPAdjustment(): void {
        this.loadingVisible = true;
        this.apAdjustmentService.addNewAPAdjustment(this.modifiedAPAdjustment).subscribe(
            () => {
                this.showSuccessMessage('AP Adjustment added successfully.');
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private createAPAdjustmentAndApplyPayment(): void {
        this.loadingVisible = true;
        const params = new ApplyPaymentWithAPCMParameterDto();
        Object.assign(params, this.modifiedAPAdjustment);
        params.apPaymentId = this.unappliedPayment.apPaymentId;

        this.apAdjustmentService.applyPaymentWithAPCM(params).subscribe(
            () => {
                this.showSuccessMessage('AP Adjustment added and payment has been applied successfully..');
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private checkValidity(): any {
        const result = { isValid: true, message: null };

        if (Utils.isNullOrUndefined(this.modifiedAPAdjustment.vendorId)) {
            result.isValid = false;
            result.message = 'Vendor is required.';
        }
        if (Utils.isNullOrUndefined(this.modifiedAPAdjustment.docDate)) {
            result.isValid = false;
            result.message = 'Document Date is required.';
        }
        if (Utils.isNullOrUndefined(this.modifiedAPAdjustment.postDate)) {
            result.isValid = false;
            result.message = 'Post Date is required.';
        }
        if (Utils.isNullOrEmpty(this.modifiedAPAdjustment.description)) {
            result.isValid = false;
            result.message = 'Description is required.';
        }

        return result;
    }

    private showSuccessMessage(message: string): void {
        notify(message, 'Success', MessageInterval.successMsgInterval);
        this.forceRefresh = true;
        this.closeAPAdjustmentEditor();
    }

    getAPAdjustmentEditor(): void {
        const baseParams: APAdjustmentParameterDto = new APAdjustmentParameterDto();

        baseParams.managementCompanyId = this.managementCompanyId;
        baseParams.communityId = Utils.notNullAndDefined(this.communityId) ? this.communityId : null;
        baseParams.id = Utils.notNullAndDefined(this.apAdjustmentId) ? this.apAdjustmentId : null;
        baseParams.clearCache = true; // we have to clear cache to get the newest data

        // Show loader
        this.loadingVisible = true;

        this.apAdjustmentService.getAPAdjustmentEditor(baseParams).subscribe(
            (result: APAdjustmentEditorResultDto) => {
                this.apAdjustmentEditor = result;
                this.glAccountNumberSource = result.glAccounts;
                this.glAccountLabelSource = result.glAccountsLabels;
                this.apAdjustment = result.adjustment;
                this.apAdjustmentId = result.adjustment.apAdjustmentId;
                this.dimensionSource = result.glDimensions;
                this.defaultOffsetGLAccountNumber = result.defaultOffsetGLAccountNumber;
                this.isEditMode = result.allowEdit;
                this.isLimitedEdit = Utils.isTrue(result.allowLimitedEdit) && Utils.isFalse(result.allowEdit);

                this.prepareAttachments();
                if (Utils.isFalse(this.isEditMode)) {
                    this.apAdjustment.docDate = new Date();
                    this.apAdjustment.postDate = new Date();
                } else {
                    this.vendorGridBoxValue = Utils.notNullAndDefined(this.apAdjustment.vendorId)
                        ? [this.apAdjustment.vendorId]
                        : [];
                    this.prepareEditorLineItems();
                }
                this.configureTitle();

                if (Utils.isTrue(this.apAdjustmentEditor.entityUsesSubCategory)) {
                    this.getSubCategories();
                }
                this.originalAdjusment = cloneDeep(this.apAdjustment);

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private configureAccountTypeSource(): void {
        this.accountTypeSource = [
            {
                name: 'account_label',
                text: 'Account Label'
            },
            {
                name: 'account_number',
                text: 'Account Number'
            }
        ];
    }

    private getSubCategories(): void {
        if (Utils.notNullOrEmpty(this.apAdjustment.items)) {
            this.apAdjustment.items.forEach((item) => {
                if (Utils.notNullAndDefined(item.glAccountNumber)) {
                    const accountType = Utils.notNullOrEmpty(item.accountLabelCode)
                        ? 'account_label'
                        : 'account_number';
                    this.populateSubcategories(
                        item.glAccountNumber,
                        item,
                        item.subCategoryCode,
                        accountType,
                        false,
                        false
                    );
                }
            });
        }
    }

    private populateSubcategories(
        glAccountNumber: string,
        rawItem: any,
        selectedSubcategoryCode: string = null,
        selectedAccountType,
        clearCache = false,
        showInformation: boolean
    ): void {
        const baseParam: SubCategoryByGlAccountParametersDto = new SubCategoryByGlAccountParametersDto();
        baseParam.managementCompanyId = this.managementCompanyId;
        baseParam.communityId = this.communityId;
        baseParam.glAccountNumber = glAccountNumber;
        baseParam.clearCache = clearCache;

        this.loadingVisible = true;
        this.apAdjustmentService.getSubCategories(baseParam).subscribe(
            (result: GLSubcategorySelectorResultDto[]) => {
                if (Utils.isTrue(this.apAdjustmentEditor.entityUsesSubCategory)) {
                    const { rawItem: updatedRawItem, infoMessage } = this.cirabooksHelperService.processSubcategories(
                        rawItem,
                        result,
                        selectedSubcategoryCode,
                        selectedAccountType,
                        this.glAccountLabelSource,
                        this.glAccountNumberSource
                    );

                    // Replace the original rawItem with the updated one
                    rawItem = updatedRawItem;

                    if (Utils.isTrue(showInformation) && Utils.notNullOrEmpty(infoMessage)) {
                        const dialog = this.helperService.getOkDialog('Information', infoMessage);
                        dialog.show();
                    }
                }

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateSubcategoriesFromVendorTemplate(
        item: any,
        glAccountNumber: string,
        subcategoryId: number,
        payment: UnappliedPaymentResultDto
    ): void {
        const baseParam: SubCategoryByGlAccountParametersDto = new SubCategoryByGlAccountParametersDto();
        baseParam.managementCompanyId = payment.managementCompanyId;
        baseParam.communityId = Utils.notNullAndDefined(payment.communityId) ? payment.communityId : null;
        baseParam.glAccountNumber = glAccountNumber;
        baseParam.clearCache = false;

        this.loadingVisible = true;
        this.apAdjustmentService.getSubCategories(baseParam).subscribe(
            (result: GLSubcategorySelectorResultDto[]) => {
                item.subCategorySource = result; // for the dropdown

                if (Utils.notNullAndDefined(subcategoryId)) {
                    const subCategory = item.subCategorySource.find((d) => d.id === subcategoryId);

                    item.subCategoryCode = Utils.notNullAndDefined(subCategory) ? subCategory.code : null;
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private addRawLineItem(): APAdjustmentItemRawDto {
        const item: APAdjustmentItemRawDto = new APAdjustmentItemRawDto();
        item.amount = 0;
        item.openBalance = 0;
        item.apAdjustmentItemId = null;
        item['itemId'] = Utils.notNullOrEmpty(this.apAdjustment.items)
            ? Math.max.apply(
                  Math,
                  this.apAdjustment.items.map((o) => {
                      return o['itemId'];
                  })
              ) + 1
            : 1;
        item.crudAction = ActionConstants.ADD;
        item.apAdjustmentId = this.apAdjustment.apAdjustmentId;
        item.description = this.apAdjustment.description;

        item['selectedAccountName'] = '';
        item['disableSubCategory'] = false;
        item['subCategorySource'] = [];
        item['accountType'] = '';

        this.apAdjustment.items.push(item);

        this.prepareDimensions();

        // if it is required dimension match for ap and payment has a required dimension + code, we use it for the item dimension
        if (Utils.notNullOrEmpty(this.paymentRequiredDimensions) && Utils.notNullOrEmpty(item.dimensions)) {
            this.paymentRequiredDimensions.forEach((paymentRequiredDimension: DimensionOptionResultDto) => {
                const itemDimension = item.dimensions.find((itemDim: DimensionOptionCodeRawDto) =>
                    Utils.equalsIgnoreCase(paymentRequiredDimension.dimensionCode, itemDim.dimensionCode)
                );

                if (Utils.notNullAndDefined(itemDimension)) {
                    itemDimension.dimensionOptionCode = paymentRequiredDimension.dimensionOptionCode;
                    item[itemDimension.dimensionCode] = paymentRequiredDimension.dimensionOptionCode; // adjustmentItem['FUND'] = 'OPER' for dimension option selection in the UI
                }
            });
        }

        return item;
    }

    private prepareEditorLineItems(): void {
        const apAdjustmentItems: APAdjustmentItemRawDto[] = [];
        let index = 0;
        this.apAdjustment.items.forEach((item) => {
            index += 1;
            let obj: APAdjustmentItemRawDto = new APAdjustmentItemRawDto();
            obj = Object.assign(obj, item);
            obj['itemId'] = index;

            const selectedGLAccount = this.glAccountNumberSource.find((d) =>
                Utils.equals(d.glAccountNumber, item.glAccountNumber)
            );
            if (Utils.notNullAndDefined(selectedGLAccount)) {
                obj.glAccountNumber = selectedGLAccount.glAccountNumber;
                obj['selectedAccountName'] = `${selectedGLAccount.title};${selectedGLAccount.glAccountNumber}`;
                this.populateSubcategories(
                    selectedGLAccount.glAccountNumber,
                    obj,
                    item.subCategoryCode,
                    'account_number',
                    false,
                    true
                );
            }

            const selectedOffsetGLAccount = this.glAccountNumberSource.find((d) =>
                Utils.equals(d.glAccountNumber, item.offsetGLAccountNumber)
            );
            if (Utils.notNullAndDefined(selectedOffsetGLAccount)) {
                obj.offsetGLAccountNumber = selectedOffsetGLAccount.glAccountNumber;
            }

            const selectedGLAccountLabel = this.glAccountLabelSource.find(
                (d) =>
                    Utils.notNullAndDefined(item.accountLabelCode) &&
                    Utils.equalsIgnoreCase(d.code, item.accountLabelCode)
            );
            if (Utils.notNullAndDefined(selectedGLAccountLabel)) {
                obj.accountLabelCode = selectedGLAccountLabel.code;
                obj[
                    'selectedAccountName'
                ] = `${selectedGLAccountLabel.title};${selectedGLAccountLabel.glAccountNumber}`;
                if (Utils.notNullAndDefined(selectedGLAccountLabel.subCategoryCode)) {
                    obj.subCategoryCode = selectedGLAccountLabel.subCategoryCode;
                    obj['disableSubCategory'] = true;
                    this.populateSubcategories(
                        selectedGLAccountLabel.glAccountNumber,
                        obj,
                        item.subCategoryCode,
                        'account_label',
                        false,
                        true
                    );
                }
            }

            apAdjustmentItems.push(obj);
        });

        this.apAdjustment.items = apAdjustmentItems;
        this.prepareDimensions();
    }

    private prepareDimensions(): void {
        if (Utils.notNullOrEmpty(this.apAdjustment.items)) {
            this.apAdjustment.items.forEach((item) => {
                this.dimensionSource.forEach((dimNode) => {
                    let itemDimension: DimensionOptionCodeRawDto;

                    if (Utils.notNullOrEmpty(item.dimensions)) {
                        itemDimension = item.dimensions.find((d) =>
                            Utils.equalsIgnoreCase(d.dimensionCode, dimNode.code)
                        );
                    }
                    if (Utils.notNullAndDefined(itemDimension)) {
                        if (Utils.notNullAndDefined(itemDimension.dimensionOptionCode)) {
                            const selectedNode = dimNode.options.find((d) =>
                                Utils.equalsIgnoreCase(d.code, itemDimension.dimensionOptionCode)
                            );
                            item[itemDimension.dimensionCode] = Utils.notNullAndDefined(selectedNode)
                                ? selectedNode.code
                                : null;
                        } else if (Utils.isNullOrUndefined(item[itemDimension.dimensionCode])) {
                            item[itemDimension.dimensionCode] = null;
                        }
                    } else {
                        const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                        obj.dimensionCode = dimNode.code;
                        obj.dimensionOptionCode = null;
                        item[dimNode.code] = null; // ex: 'OWNER_TYPE' = null
                        if (Utils.isNullOrUndefined(item.dimensions)) {
                            // fix a bug when adding a new line item in edit mode.
                            item.dimensions = [];
                        }
                        item.dimensions.push(obj);
                    }
                });
            });
        }
    }

    private closeAPAdjustmentEditor(): void {
        this.apAdjustment = null;
        this.visible = false;
        this.onClose.emit(this.forceRefresh);
    }

    private checkLineItemValidity(): any {
        const resultStatus = { isValid: true, message: null };
        let validLineItems = 0;

        this.apAdjustment.items.forEach((data, index) => {
            if (Utils.notEqualsIgnoreCase(data.crudAction, ActionConstants.DELETE)) {
                validLineItems += 1;
                const itemIndex = index + 1;
                if (Utils.isNullOrUndefined(data.glAccountNumber)) {
                    resultStatus.isValid = false;
                    resultStatus.message = `The GL Account number is Required for ItemId #${itemIndex}`;
                } else if (
                    Utils.isFalse(data.isAccountSubcategoryValid) &&
                    Utils.notNullAndDefined(data.accountSubcategoryValidationMessage)
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = `Invalid Line Item as ${data.accountSubcategoryValidationMessage}`;
                }

                this.dimensionSource.forEach((dimension) => {
                    if (Utils.isTrue(dimension.required)) {
                        const itemDimension = data.dimensions.find((d) =>
                            Utils.equalsIgnoreCase(d.dimensionCode, dimension.code)
                        );
                        if (
                            Utils.notNullAndDefined(itemDimension) &&
                            Utils.isNullOrUndefined(itemDimension.dimensionOptionCode)
                        ) {
                            resultStatus.isValid = false;
                            resultStatus.message = `${dimension.code} is Required for ItemId #${itemIndex}`;
                        }
                    }
                });
            }
        });
        if (Utils.equals(validLineItems, 0)) {
            resultStatus.isValid = false;
            resultStatus.message = `Items list cannot be null or empty.`;
        }
        return resultStatus;
    }

    showForApplyPaymentWithAPCM(payment: UnappliedPaymentResultDto): void {
        this.forceRefresh = false; // reset so that if the user clicks "close" button, it won't refresh the parent UI
        this.loadAPAdjustmentEditorForApplyPaymentWithAPCM(payment);
    }

    private loadAPAdjustmentEditorForApplyPaymentWithAPCM(payment: UnappliedPaymentResultDto): void {
        this.managementCompanyId = payment.managementCompanyId;
        this.communityId = payment.communityId;
        this.unappliedPayment = payment;

        this.requireDimensionMatch = false;
        this.paymentRequiredDimensions = []; // reset

        const params = new APAdjustmentEditorForApplyPaymentWithAPCMParameterDto();
        params.managementCompanyId = payment.managementCompanyId;
        params.communityId = Utils.notNullAndDefined(payment.communityId) ? payment.communityId : null;
        params.vendorCode = payment.vendorCode;
        params.apPaymentId = payment.apPaymentId;
        params.clearCache = true;

        this.loadingVisible = true;
        this.apAdjustmentService.getEditorForApplyPaymentWithAPCM(params).subscribe(
            (data: APAdjustmentEditorForApplyPaymentWithAPCMResultDto) => {
                const editor = data.editor;
                this.requireDimensionMatch = data.requireDimensionMatch;
                this.paymentRequiredDimensions = data.paymentRequiredDimensions;

                this.apAdjustmentEditor = editor;
                this.disabledAdjustmentType = true; // apAdjustmentEditor.allowEdit is true, but we need to disable/readonly to AdjustmentType only. As discussed with Sandeep on ANG-1962

                this.glAccountNumberSource = editor.glAccounts;
                this.glAccountLabelSource = editor.glAccountsLabels;
                this.apAdjustment = editor.adjustment;
                this.dimensionSource = editor.glDimensions;
                this.defaultOffsetGLAccountNumber = editor.defaultOffsetGLAccountNumber;
                this.apAdjustment.docDate = new Date();
                this.apAdjustment.postDate = new Date();

                // assign default values for "ApplyPaymentWithAPCM"
                this.apAdjustment.description = `Apply Payment (Ref #: ${payment.referenceNumber}) w/ APCM`;
                // assign vendor to the adjustment with the vendor from the payment
                this.vendorGridBoxValue = Utils.notNullAndDefined(payment.vendorId) ? [payment.vendorId] : [];

                const useSubCategory = Utils.isTrue(this.apAdjustmentEditor.entityUsesSubCategory);

                // populate adjustment items if there is vendor template.
                if (!Utils.isNullOrEmpty(data.vendorLineItemTemplates)) {
                    this.selectedAccountType = 'account_number';

                    data.vendorLineItemTemplates.forEach((vendorItemTemplate: VendorLineItemTemplateRawDto) => {
                        const newAdjustmentItem = this.addRawLineItem(); // add a new row with default values for line item
                        newAdjustmentItem.offsetGLAccountNumber = this.defaultOffsetGLAccountNumber;
                        newAdjustmentItem.description = vendorItemTemplate.description;

                        // populate gl account
                        const glAccountInfo = editor.glAccounts.find(
                            (d) => d.subAccountID === vendorItemTemplate.subAccountId
                        );

                        if (Utils.notNullOrEmpty(glAccountInfo)) {
                            newAdjustmentItem.glAccountNumber = glAccountInfo.glAccountNumber;
                            newAdjustmentItem[
                                'selectedAccountName'
                            ] = `${glAccountInfo.title};${glAccountInfo.glAccountNumber}`;
                        }

                        // populate sub cat
                        if (Utils.isTrue(useSubCategory) && !!newAdjustmentItem.glAccountNumber) {
                            this.populateSubcategoriesFromVendorTemplate(
                                newAdjustmentItem,
                                newAdjustmentItem.glAccountNumber,
                                vendorItemTemplate.subCategoryId,
                                payment
                            );
                        }

                        // populate dimensions
                        if (Utils.notNullAndDefined(vendorItemTemplate.dimensions)) {
                            this.assignDimensionOptionsFromVendorTemplate(
                                vendorItemTemplate.dimensions,
                                newAdjustmentItem
                            );
                        }
                    });
                } else {
                    // no vendor template, add a row by default
                    this.addRawLineItem();
                }

                if (!!this.apAdjustment.items && this.apAdjustment.items.length > 0) {
                    const unappliedAmount = Math.abs(payment.unappliedAmount);
                    this.apAdjustment.items[0].amount = unappliedAmount; //  assign first item's amount by payment's unapplied amount as discussion
                    this.apAdjustment.items[0].openBalance = unappliedAmount;
                }

                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private assignDimensionOptionsFromVendorTemplate(
        dimensionCsv: string,
        adjustmentItem: APAdjustmentItemRawDto
    ): void {
        const dimOptions = dimensionCsv.split(','); // ex: FUND|OPER,OWNER_TYPE:H
        if (dimOptions.length > 0) {
            dimOptions.forEach((dimOption: string) => {
                const arr = dimOption.split('|');

                if (arr.length === 2) {
                    const dimCode = arr[0]; // ex: FUND
                    const optionCode = arr[1]; // ex: OPER

                    // check if item has this dimension code.  adjustmentItem.dimensions has been added via the addRawLineItem method
                    let itemDimension: DimensionOptionCodeRawDto;
                    if (Utils.notNullAndDefined(adjustmentItem) && Utils.notNullOrEmpty(adjustmentItem.dimensions)) {
                        itemDimension = adjustmentItem.dimensions.find((d: DimensionOptionCodeRawDto) =>
                            Utils.equalsIgnoreCase(d.dimensionCode, dimCode)
                        );
                    }

                    let paymentRequiredDimensionMatch: DimensionOptionResultDto;
                    if (Utils.notNullOrEmpty(this.paymentRequiredDimensions)) {
                        paymentRequiredDimensionMatch = this.paymentRequiredDimensions.find(
                            (d: DimensionOptionResultDto) => Utils.equalsIgnoreCase(d.dimensionCode, dimCode)
                        );
                    }

                    if (Utils.notNullAndDefined(itemDimension)) {
                        // if it is required dimension match for ap and payment has a required dimension + code, we use it for the item dimension
                        if (Utils.notNullAndDefined(paymentRequiredDimensionMatch)) {
                            itemDimension.dimensionOptionCode = paymentRequiredDimensionMatch.dimensionOptionCode;
                            adjustmentItem[dimCode] = paymentRequiredDimensionMatch.dimensionOptionCode; // adjustmentItem['FUND'] = 'OPER' for dimension option selection in the UI
                        } else {
                            // assign dimension option code for the matching dimension
                            itemDimension.dimensionOptionCode = optionCode; // for posting to the web api to create ap adjustment
                            adjustmentItem[dimCode] = optionCode; // adjustmentItem['FUND'] = 'OPER' for dimension option selection in the UI
                        }
                    }
                }
            });
        }
    }

    onCellPrepared(event: any): void {
        if (Utils.equalsIgnoreCase(event.rowType, 'data')) {
            let highLight = false;
            const dataField = event.column.dataField;
            if (Utils.equalsIgnoreCase(dataField, 'openBalance')) {
                highLight = true;
            }

            // if this is a required dimension column (ex: FUND), and require Dimension must Match, and paymentRequiredDimensions has a dimension option for required dimension, we disable the column editing
            if (Utils.notNullOrEmpty(this.dimensionSource)) {
                const dimension = this.dimensionSource.find((d) => Utils.equalsIgnoreCase(d.code, dataField));
                if (
                    Utils.notNullAndDefined(dimension) &&
                    Utils.isTrue(dimension.required) &&
                    Utils.isTrue(this.requireDimensionMatch) &&
                    Utils.notNullOrEmpty(this.paymentRequiredDimensions)
                ) {
                    const paymentRequiredDimension = this.paymentRequiredDimensions.find(
                        (d: DimensionOptionResultDto) => Utils.equalsIgnoreCase(d.dimensionCode, dataField)
                    );
                    // payment has a dimension option code (Ex: OPER) for this dimension code (ex: FUND)
                    if (
                        Utils.notNullAndDefined(paymentRequiredDimension) &&
                        Utils.notNullOrEmpty(paymentRequiredDimension.dimensionOptionCode)
                    ) {
                        highLight = true;
                    }
                }
            }

            if (highLight) {
                // highlight non-editing field
                event.cellElement.classList.add('cc-light-grey-background');
            }
        }
    }

    get allowChangeRequiredDimension(): boolean {
        if (Utils.isTrue(this.requireDimensionMatch) && Utils.notNullOrEmpty(this.paymentRequiredDimensions)) {
            return false;
        }
        return true;
    }
}
